/* eslint-disable complexity */
/* eslint-disable @typescript-eslint/prefer-ts-expect-error */
/* eslint-disable @typescript-eslint/ban-ts-comment */
import { createRef, useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FIELD_QUANTITY, REQUIRED_TEXT_ERROR } from '@components/dynamic-input-fields/src/utils';
import { ErrorMessage } from '@components/error/src/error-message';
import { Input } from '@components/input';
import { InputAccessValidityDates } from '@components/input-access-validity-dates';
import {
  CHANNELS_SALES_QUOTA_ALL,
  CHANNELS_SALES_QUOTA_CHANNEL,
  CHANNELS_SALES_QUOTA_UNLIMITED,
  SelectedChannel,
} from '@components/input-select-channels/src/input-select-channels.type';
import { InputSelectNestedCheckbox } from '@components/input-select-nested-checkbox';
import { InputWithTitleAndSubtitle } from '@components/input-with-title-and-subtitle';
import {
  INPUT_CHECKBOX,
  INPUT_LEFT,
} from '@components/input-with-title-and-subtitle/src/input-with-title-and-subtitle.type';
import { LabelPrimary } from '@components/label';
import { LabelInfo } from '@components/label/src/label-info';
import { NestedCheckboxItem } from '@components/nested-checkbox-list/src/nested-checkbox-list.type';
import { getChildrenIds } from '@components/nested-checkbox-list/utils';
import { Select, SelectOption } from '@components/select';
import { Toggle } from '@components/toggle';
import { VatRateInput } from '@components/vat-rate-input';
import { DEFAULT_MAX_ITEMS_PER_TRANSACTION } from '@constants/admissions';
import { DRINKS_INCLUDED_CLUB_IDS } from '@constants/drinksincluded-club-ids';
import { FeatureFlagContext } from '@contexts/featureFlag/featureFlag-context';
import {
  ADMISSION_TYPE_BOTTLE_SERVICE,
  ADMISSION_TYPE_GUEST_LIST,
  ADMISSION_TYPE_TICKET,
  BOOKING_FORM_FIELDS,
  TICKET_HOLDER_FORM_FIELDS,
} from '@domain/admission';
import { useGetEventTeamChannels } from '@hooks/team-channel/use-get-event-team-channels';
import { calculateDefaultMaxCapacity } from '@utils/calculate-dafult-max-capacity/calculate-default-max-capacity';
import { calculateMaxItemsPerTransation } from '@utils/calculate-max-items-per-transation/calculate-max-items-per-transation';
import { formatEventTeamsToNestedCheckboxList } from '@utils/format-event-teams-to-nested-checkbox-list';
import { isEventInThePast } from '@utils/is-event-in-the-past';
import { fromBackend } from '@utils/map-date/from-backend';
import { cannotBeNegative } from '@utils/validations/cannot-be-negative';
import { max } from '@utils/validations/max';
import { maxDecimals } from '@utils/validations/max-decimals';
import { min } from '@utils/validations/min';
import classNames from 'classnames';

import { InputFormFields } from '../../input-form-fields/src/input-form-fields';

import { Form } from './admission.type';
import { AdvancedFormProps, TeamQuantity } from './advanced.form.types';
import { HierarchicalChannelLimits } from './hierarchical-channel-limits';

export const AdvancedForm = ({
  offer,
  admissionChannels,
  setAdmissionsChannels,
  type,
  clubState,
  event,
  maxEndDate,
  isDDPInputDisabled,
  isDuplicatedAdmission,
  setValidityglobalError,
  validityglobalError,
  teamQuantities,
  setTeamQuantities,
  ticketsLeftThresholdPlaceholder,
}: AdvancedFormProps) => {
  const { t } = useTranslation();
  const methods = useFormContext<Form>();
  const { register, unregister, control, watch, formState, setValue, getValues, setError, clearErrors, trigger } =
    methods;
  const { errors } = formState;
  // @ts-ignore
  const quantity = watch(FIELD_QUANTITY);
  const isPrivate = watch('isPrivate');
  const isForAllChannels = watch('isForAllChannels');
  const isNominative = watch('isNominative');
  const salesQuota = watch('salesQuota');

  const ref = createRef<HTMLDivElement>();

  const isPastEvent = isEventInThePast(event);

  const { data: eventTeams, isLoading: isLoadingEventTeams } = useGetEventTeamChannels({ eventId: event?.id });

  // Stable channel IDs reference — only changes when channels are added/removed, not when values change
  const admissionChannelIds = useMemo(() => admissionChannels.map((c) => c.id).join(','), [admissionChannels]);

  const nestedCheckboxData = useMemo(() => {
    if (eventTeams) {
      const data = formatEventTeamsToNestedCheckboxList(eventTeams);

      // Filter to only include channels that exist in admissionChannels (enabled channels)
      const channelIds = new Set(admissionChannelIds.split(','));

      return data
        .map((item) => {
          if (item.children.length > 0) {
            const filtered = item.children.filter((child) => channelIds.has(child.id));
            return filtered.length > 0 ? { ...item, children: filtered } : null;
          }
          return channelIds.has(item.id) ? item : null;
        })
        .filter(Boolean) as typeof data;
    }

    // Preset context: no event available, use flat admissionChannels as top-level items
    return admissionChannels.map((channel) => ({
      id: channel.id,
      name: channel.name,
      slug: channel.slug,
      children: [],
    }));
  }, [eventTeams, admissionChannelIds, admissionChannels]);

  const selectedChannelIds = useMemo(
    () => admissionChannels.filter((c) => c.checked).map((c) => c.id),
    [admissionChannels]
  );

  // Refs to read latest values without triggering callback recreation
  const admissionChannelsRef = useRef(admissionChannels);
  admissionChannelsRef.current = admissionChannels;
  const nestedCheckboxDataRef = useRef<NestedCheckboxItem[]>(nestedCheckboxData);
  nestedCheckboxDataRef.current = nestedCheckboxData;

  const selectedIdsWithParents = useMemo(() => {
    const channelIdSet = new Set(selectedChannelIds);
    const allIds = [...selectedChannelIds];

    nestedCheckboxData.forEach((item) => {
      if (item.children.length > 0) {
        const allChildrenSelected = item.children.every((child) => channelIdSet.has(child.id));
        if (allChildrenSelected) allIds.push(item.id);
      }
    });
    return allIds;
  }, [selectedChannelIds, nestedCheckboxData]);

  useEffect(() => {
    if ((isPrivate && offer?.settings.isPrivate === false) || (isPrivate && !offer))
      setValue('isForAllChannels', true, { shouldDirty: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPrivate]);

  useEffect(() => {
    if (salesQuota !== CHANNELS_SALES_QUOTA_ALL) unregister('limitAllChannels');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [salesQuota]);

  // @ts-ignore
  const { fields: ticketHolderFormFields, append: appendHolderFormField } = useFieldArray({
    control,
    name: TICKET_HOLDER_FORM_FIELDS,
  });

  const { fields: bookingFormFields, append: appendBookingFormField } = useFieldArray({
    control,
    name: BOOKING_FORM_FIELDS,
  });

  const handleNewTicketHolderFormField = (label: string) => {
    appendHolderFormField({
      name: label,
      dataType: 'text',
      isRequired: true,
      disabled: false,
    });
  };

  const handleNewTicketBookingFormField = (label: string, maxNum?: number, minNum?: number, dataType?: string) => {
    if (!dataType) dataType = 'text';
    appendBookingFormField({
      name: label,
      dataType,
      isRequired: true,
      disabled: false,
      max: maxNum,
      min: minNum,
    });
  };

  const handleChannelsChange = useCallback(
    (ids: string[]) => {
      const childrenIds = new Set(getChildrenIds(ids, nestedCheckboxDataRef.current));

      const allChannels = admissionChannelsRef.current.map((channel) => ({
        ...channel,
        checked: childrenIds.has(channel.id),
      }));

      setAdmissionsChannels(allChannels);
      setValue('isChannelsDirty', true, { shouldDirty: true });

      const checkedCount = allChannels.filter((ch) => ch.checked).length;
      if (checkedCount === 0) {
        setError('salesQuota', { type: 'custom', message: 'no channel selected' });
      } else {
        clearErrors('salesQuota');
      }
    },
    [setAdmissionsChannels, setValue, setError, clearErrors]
  );

  const hasOfferSoldItems = useMemo(() => {
    return offer ? offer.soldItems > 0 : false;
  }, [offer]);

  const selectedChannels: SelectedChannel[] = useMemo(() => {
    return admissionChannels
      .map((channel, originalIndex) => ({ ...channel, originalIndex }))
      .filter((channel) => !!channel.checked);
  }, [admissionChannels]);

  const teamLookup = useMemo(() => new Map((teamQuantities ?? []).map((tq) => [tq.teamId, tq])), [teamQuantities]);

  const validateChannelValue = useCallback(
    (val: string, selectedIndex: number, parentTeamLimit?: number | null): void => {
      const fieldPath = `channels.${selectedIndex}.value` as const;

      const isMin = min({
        value: val,
        allowEmptyValue: true,
        minValue: 1,
        message: t('offers:admissionSalesChannelsQuotaErrorMin', { number: 1 }),
      });
      const isMax = max({
        value: val,
        maxValue: quantity,
        message: t('offers:admissionSalesChannelsQuotaErrorMax', { quantity }),
      });
      const isDecimals = maxDecimals({
        value: val,
        message: t('offers:admissionSalesChannelsQuotaErrorDecimals'),
        decimals: 0,
      });
      const exceedsTeamLimit =
        parentTeamLimit !== undefined && parentTeamLimit !== null && val !== '' && Number(val) > parentTeamLimit;

      if (isMin) setError(fieldPath, { type: 'custom', message: isMin });
      else if (isMax) setError(fieldPath, { type: 'custom', message: isMax });
      else if (isDecimals) setError(fieldPath, { type: 'custom', message: isDecimals });
      else if (exceedsTeamLimit)
        setError(fieldPath, {
          type: 'custom',
          message: t('offers:channelMaxQuantityHigherThanTeamCapacityError'),
        });
      else {
        clearErrors(fieldPath);
        trigger(fieldPath);
      }
    },
    [quantity, setError, clearErrors, trigger, t]
  );

  const handleChannelLimitChange = useCallback(
    (originalIndex: number, val: string) => {
      setAdmissionsChannels((prev) =>
        prev.map((admissionChannel, admissionIndex) => {
          if (admissionIndex === originalIndex) return { ...admissionChannel, value: val };
          return admissionChannel;
        })
      );
      setValue('isChannelsDirty', true, { shouldDirty: true });

      const selectedIndex = selectedChannels.findIndex((ch) => ch.originalIndex === originalIndex);

      const currentChannel = admissionChannelsRef.current[originalIndex];
      const parentTeam = currentChannel
        ? nestedCheckboxDataRef.current.find((item) => item.children.some((child) => child.id === currentChannel.id))
        : undefined;
      const parentTeamLimit = parentTeam ? teamLookup.get(parentTeam.id)?.quantity : undefined;

      validateChannelValue(val, selectedIndex, parentTeamLimit);
    },
    [selectedChannels, setAdmissionsChannels, setValue, teamLookup, validateChannelValue]
  );

  const handleTeamLimitChange = useCallback(
    (teamId: string, val: string) => {
      if (setTeamQuantities) {
        setTeamQuantities((prev: TeamQuantity[]) => {
          const parsedQuantity = val === '' ? null : Number(val);
          const exists = prev.some((team) => team.teamId === teamId);
          if (exists) {
            return prev.map((prevTeam: TeamQuantity) =>
              prevTeam.teamId === teamId ? { ...prevTeam, quantity: parsedQuantity } : prevTeam
            );
          }
          return [...prev, { teamId, quantity: parsedQuantity }];
        });
      }
      setValue('isChannelsDirty', true, { shouldDirty: true });

      // Re-validate child channels under this team
      const parentTeam = nestedCheckboxDataRef.current.find((item) => item.id === teamId);
      if (parentTeam) {
        const parsedTeamLimit = val === '' ? null : Number(val);
        parentTeam.children.forEach((child) => {
          const channelData = admissionChannelsRef.current.find((ch) => ch.id === child.id);
          if (!channelData?.checked) return;

          const selectedIndex = selectedChannels.findIndex((ch) => ch.id === child.id);
          if (selectedIndex === -1) return;

          const channelValue = channelData.value;
          if (channelValue !== '') {
            validateChannelValue(channelValue, selectedIndex, parsedTeamLimit);
          }
        });
      }
    },
    [setTeamQuantities, setValue, selectedChannels, validateChannelValue]
  );

  const getChannelError = useCallback(
    (selectedIndex: number): string | undefined => {
      const channelError = errors.channels ? errors.channels[selectedIndex] : undefined;
      return channelError && 'value' in channelError ? channelError.value?.message : undefined;
    },
    [errors.channels]
  );

  const { isTicketsLeftThresholdEnabled } = useContext(FeatureFlagContext);
  const isClubCountryItaly = clubState.city.country.isoCode === 'ITA';
  const isTicketOrBottleService = type === ADMISSION_TYPE_BOTTLE_SERVICE || type === ADMISSION_TYPE_TICKET;
  const isDdpActive = isClubCountryItaly && isTicketOrBottleService;
  const isDrinksIncludedActive = DRINKS_INCLUDED_CLUB_IDS.includes(clubState.id);

  const extraFieldsActives = isDdpActive || isDrinksIncludedActive;

  const minValidFromDate = event?.startingTime ? fromBackend(event.startingTime, event.club.city.timezone) : undefined;

  const watchValidFromDate = watch('validFrom');
  const minValidUntilDate = useMemo(() => {
    if (watchValidFromDate) {
      return watchValidFromDate;
    }
    return minValidFromDate;
  }, [watchValidFromDate, minValidFromDate]);

  const defaultMaxCapacity = calculateDefaultMaxCapacity(type);

  const getEffectiveMaxItems = () => {
    const maxField = getValues('itemsPerTransaction.max');
    return maxField === '' ? defaultMaxCapacity : maxField;
  };

  const minItemsPerTransationRegister = register('itemsPerTransaction.min', {
    validate: {
      cannotBeNegative: (value) =>
        cannotBeNegative({
          value,
          message: t('offers:admissionMinItemsPerTransactionNegativeError'),
        }),
      maxDecimals: (value) =>
        maxDecimals({
          value,
          decimals: 0,
          message: t('offers:admissionMinItemsPerTransactionDecimalsError'),
        }),
      max: () => {
        const minItems = getValues('itemsPerTransaction.min');
        const maxValue = getEffectiveMaxItems();
        return max({
          value: minItems,
          maxValue,
          message: t('offers:admissionMinItemsPerTransactionMaxError', {
            maxValue,
          }),
        });
      },
    },
  });

  const maxItemsPerTransationRegister = register('itemsPerTransaction.max', {
    validate: {
      cannotBeNegative: (value) =>
        cannotBeNegative({
          value,
          message: t('offers:admissionMaxItemsPerTransactionNegativeError'),
        }),
      maxDecimals: (value) =>
        maxDecimals({
          value,
          decimals: 0,
          message: t('offers:admissionMaxItemsPerTransactionDecimalsError'),
        }),
      max: () => {
        const currentQuantity = getValues(FIELD_QUANTITY);
        const quantityPurchase = currentQuantity ? Number(currentQuantity) : null;
        const maxItems = getValues('itemsPerTransaction.max');
        const maxValue = calculateMaxItemsPerTransation(quantityPurchase);
        return max({
          value: maxItems,
          maxValue,
          message: t('offers:admissionMaxItemsPerTransactionMaxError', {
            maxValue,
          }),
        });
      },
      min: () => {
        const maxValue = getEffectiveMaxItems();
        const minValue = getValues('itemsPerTransaction.min');
        return min({
          value: maxValue,
          minValue,
          message: t('offers:admissionMaxItemsPerTransactionMinError', {
            minValue,
          }),
        });
      },
    },
  });

  const excludeSiaeTranslations = useMemo(() => {
    return {
      title: t('offers:excludeSiaeCheckboxTitle'),
      subtitle: t('offers:excludeSiaeCheckboxSubtitle'),
    };
  }, [t]);

  const isOfferSiaeExcluded = offer?.settings.italyRegulation?.isSiaeExcluded;

  const isExcludedSiaeCheckboxDisabled = event?.settings.isPublished && isOfferSiaeExcluded;

  const showExcludedSiaeCheckbox =
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    !!event?.companyClub.company?.isAllowedToExcludeSiaeOffers &&
    type !== ADMISSION_TYPE_GUEST_LIST &&
    (!offer || isExcludedSiaeCheckboxDisabled || isDuplicatedAdmission || !event?.settings.isPublished);

  return (
    <div id='advanced-form' className='overflow-hidden transition-all duration-700 ease-in-out'>
      <div ref={ref}>
        <hr className='my-12 border-t border-extra-soft' />
        <div className='grid grid-cols-2 md:grid-cols-3 gap-6 items-start mb-8'>
          <div>
            <LabelPrimary
              className='text-left '
              htmlFor='itemsPerTransaction.min'
              hasError={!!errors.itemsPerTransaction?.min}>
              {t('offers:admissionMinItemsPerTransactionLabel')}
            </LabelPrimary>

            <Input
              id='itemsPerTransaction.min'
              type='number'
              step={1}
              min='1'
              max={DEFAULT_MAX_ITEMS_PER_TRANSACTION}
              placeholder={'1'}
              disabled={isPastEvent}
              readOnly={isPastEvent}
              hasError={!!errors.itemsPerTransaction?.min}
              {...minItemsPerTransationRegister}
              onChange={(e) => {
                minItemsPerTransationRegister.onChange(e);
                trigger('itemsPerTransaction.max');
              }}
            />
            <ErrorMessage name='itemsPerTransaction.min' />
          </div>
          <div>
            <LabelPrimary
              className='text-left'
              htmlFor='itemsPerTransaction.max'
              hasError={!!errors.itemsPerTransaction?.max}>
              {t('offers:admissionMaxItemsPerTransactionLabel')}
            </LabelPrimary>

            <Input
              id='itemsPerTransaction.max'
              type='number'
              step={1}
              min='1'
              max={DEFAULT_MAX_ITEMS_PER_TRANSACTION}
              disabled={isPastEvent}
              readOnly={isPastEvent}
              placeholder={defaultMaxCapacity}
              hasError={!!errors.itemsPerTransaction?.max}
              {...maxItemsPerTransationRegister}
              onChange={(e) => {
                maxItemsPerTransationRegister.onChange(e);
                trigger('itemsPerTransaction.min');
              }}
            />
            <ErrorMessage name='itemsPerTransaction.max' />
          </div>

          {isTicketsLeftThresholdEnabled && (
            <div>
              <LabelInfo htmlFor='ticketsLeftThreshold' text={t('offers:admissionTicketsLeftThresholdInfo')}>
                {t('offers:admissionTicketsLeftThresholdLabel')}
              </LabelInfo>
              <Input
                id='ticketsLeftThreshold'
                type='number'
                placeholder={ticketsLeftThresholdPlaceholder}
                min='0'
                step='1'
                hasError={!!errors.ticketsLeftThreshold}
                disabled={isPastEvent}
                readOnly={isPastEvent}
                {...register('ticketsLeftThreshold', {
                  validate: {
                    cannotBeNegative: (value) =>
                      cannotBeNegative({
                        value,
                        message: t('offers:admissionTicketsLeftThresholdCannotBeNegativeError'),
                      }),
                    maxDecimals: (value) =>
                      maxDecimals({
                        value,
                        message: t('offers:admissionTicketsLeftThresholdDecimalsError'),
                        decimals: 0,
                      }),
                  },
                })}
              />
              <ErrorMessage name='ticketsLeftThreshold' />
            </div>
          )}

          {event && extraFieldsActives && isDdpActive && (
            <div>
              <LabelPrimary htmlFor='ddp' hasError={!!errors.ddp}>
                {t('offers:admissionDDPLabel')}
              </LabelPrimary>
              <Input
                id='ddp'
                type='number'
                placeholder={t('offers:admissionPricePlaceholder')}
                min='0'
                step='.01'
                readOnly={hasOfferSoldItems || isPastEvent || isDDPInputDisabled}
                disabled={isPastEvent || isDDPInputDisabled}
                hasError={!!errors.ddp}
                {...register('ddp', {
                  validate: {
                    cannotBeNegative: (value) =>
                      cannotBeNegative({
                        value,
                        message: t('offers:admissionDDPCannotBeNegativeError'),
                      }),
                    maxDecimals: (value) =>
                      maxDecimals({
                        value,
                        message: t('offers:admissionDDPDecimalsError'),
                        decimals: 2,
                      }),
                  },
                })}
              />
              <ErrorMessage name='ddp' />
            </div>
          )}
          {event && showExcludedSiaeCheckbox && (
            <div className='col-span-2 md:col-span-3'>
              <InputWithTitleAndSubtitle
                translations={excludeSiaeTranslations}
                controllerName='isSiaeExcluded'
                inputType={INPUT_CHECKBOX}
                inputPosition={INPUT_LEFT}
                textWidthClassname='w-max'
                disabled={isExcludedSiaeCheckboxDisabled}
                checked={isOfferSiaeExcluded}
              />
            </div>
          )}
          {event && extraFieldsActives && isDrinksIncludedActive && (
            <div>
              <LabelPrimary htmlFor='drinksIncluded' hasError={!!errors.drinksIncluded}>
                {t('offers:admissionDrinksIncludedLabel')}
              </LabelPrimary>
              <Input
                id='drinksIncluded'
                type='number'
                placeholder={t('offers:admissionDrinksIncludedPlaceholder')}
                min='0'
                step='1'
                hasError={!!errors.drinksIncluded}
                disabled={isPastEvent}
                readOnly={isPastEvent}
                {...register('drinksIncluded', {
                  validate: {
                    cannotBeNegative: (value) =>
                      cannotBeNegative({
                        value,
                        message: t('offers:admissionDrinksIncludedCannotBeNegativeError'),
                      }),
                    maxDecimals: (value) =>
                      maxDecimals({
                        value,
                        message: t('offers:admissionDrinksIncludedDecimalsError'),
                        decimals: 0,
                      }),
                  },
                })}
              />
              <ErrorMessage name='drinksIncluded' />
            </div>
          )}
        </div>

        {event && (
          <div className='my-12'>
            <InputAccessValidityDates
              timezone={clubState.city.timezone}
              isEventInThePast={isPastEvent}
              minValidFromDate={minValidFromDate}
              minValidUntilDate={minValidUntilDate}
              maxEndDate={maxEndDate}
              validityglobalError={validityglobalError}
              setValidityglobalError={setValidityglobalError}
            />
          </div>
        )}

        <div className='flex flex-wrap justify-between md:flex-row gap-4'>
          <div>
            <LabelInfo text={t('offers:admissionTicketIsForAllChannelsTooltip')}>
              {t('offers:admissionTicketIsForAllChannelsLabel')}
            </LabelInfo>

            <Controller
              name='isForAllChannels'
              control={control}
              render={({ field }) => (
                <Toggle
                  isEnabledText={t('offers:admissionTicketIsForAllChannelsOpen')}
                  isNotEnabledText={t('offers:admissionTicketIsForAllChannelsLimited')}
                  {...field}
                  disabled={isPrivate || isPastEvent}
                />
              )}
            />
          </div>

          <div>
            <LabelInfo htmlFor='isPrivate' text={t('offers:admissionTicketIsPrivateTooltip')}>
              {t('offers:admissionTicketIsPrivateLabel')}
            </LabelInfo>

            <Controller
              name='isPrivate'
              control={control}
              render={({ field }) => (
                <Toggle
                  isEnabledText={t('offers:admissionTicketIsPrivatePublic')}
                  isNotEnabledText={t('offers:admissionTicketIsPrivateImportOnly')}
                  {...field}
                  disabled={isPastEvent}
                />
              )}
            />
          </div>
        </div>

        <div
          className={classNames('mt-8', {
            block: isForAllChannels,
            hidden: !isForAllChannels,
          })}>
          <LabelPrimary htmlFor='accessCode' hasError={false}>
            {t('offers:admissionTicketAccessCodeLabel')}
          </LabelPrimary>

          <Input
            id='accessCode'
            placeholder={t('offers:admissionTicketAccessCodePlaceholder')}
            hasError={!!errors.accessCode}
            disabled={isPastEvent}
            readOnly={isPastEvent}
            {...register('accessCode', {
              validate: {
                isRegExpValid: (value) => {
                  return /^\w*$/.test(value ?? '');
                },
              },
            })}
          />

          <ErrorMessage name='accessCode' />
        </div>

        <div
          className={classNames('mt-8', {
            block: isForAllChannels,
            hidden: !isForAllChannels,
          })}>
          <LabelInfo htmlFor='isPrivate' text={t('offers:admissionTicketSalesChannelTooltip')}>
            {t('offers:admissionTicketSalesChannelLabel')}
          </LabelInfo>
          <div>
            <InputSelectNestedCheckbox
              key={nestedCheckboxData.map((d) => d.id).join(',')}
              data={nestedCheckboxData}
              selectedIds={selectedIdsWithParents}
              onChange={handleChannelsChange}
              emptySelectText={t('offers:admissionTicketSalesChannelsNoneSelected')}
              canSelectAll
              selectAllText={t('offers:selectAllLabel')}
              selectAllId='select-all-channels'
              canSearch
              searchText={t('offers:admissionTicketSalesChannelsSearchPlaceholder')}
              clearable
              clearText={t('offers:deselectAllLabel')}
              disabled={isPastEvent || isLoadingEventTeams}
              listHeightClassName='max-h-110 sm:max-h-130'
            />
          </div>

          {!!selectedChannels.length && (
            <div className='mt-5 ml-4'>
              <LabelPrimary htmlFor='salesQuota'>{t('offers:admissionTicketSalesQuotaLabel')}</LabelPrimary>

              <Select
                {...register('salesQuota', {
                  required: t(REQUIRED_TEXT_ERROR),
                  onChange: () => setValue('isChannelsDirty', true, { shouldDirty: true }),
                })}
                disabled={isPastEvent}
                readOnly={isPastEvent}
                hasError={!!errors.salesQuota}>
                <SelectOption
                  value={CHANNELS_SALES_QUOTA_UNLIMITED}
                  label={t('offers:admissionTicketSalesQuotaUnlimitedOption')}
                />
                <SelectOption
                  value={CHANNELS_SALES_QUOTA_CHANNEL}
                  label={t('offers:admissionTicketSalesQuotaLimitByChannelOption')}
                />
                <SelectOption
                  value={CHANNELS_SALES_QUOTA_ALL}
                  label={t('offers:admissionTicketSalesQuotaLimitChannelAllOption')}
                />
              </Select>

              <ErrorMessage name='salesQuota' />
            </div>
          )}

          {salesQuota === CHANNELS_SALES_QUOTA_CHANNEL && (
            <div className='mt-6 ml-8'>
              <HierarchicalChannelLimits
                nestedCheckboxData={nestedCheckboxData}
                selectedChannels={selectedChannels}
                teamQuantities={teamQuantities ?? []}
                disabled={isPastEvent}
                onChannelLimitChange={handleChannelLimitChange}
                onTeamLimitChange={handleTeamLimitChange}
                getChannelError={getChannelError}
              />
            </div>
          )}
          {salesQuota === CHANNELS_SALES_QUOTA_ALL && (
            <>
              <div className='mt-6 ml-8'>
                <LabelPrimary htmlFor='salesQuota'>
                  {t('offers:admissionTicketSalesQuotaLimitByChannelLabel')}
                </LabelPrimary>

                <div className='w-48'>
                  <Input
                    id='limitAllChannels'
                    type='number'
                    step={1}
                    min='1'
                    hasError={!!errors.limitAllChannels}
                    disabled={isPastEvent}
                    readOnly={isPastEvent}
                    {...register('limitAllChannels', {
                      required: t('offers:admissionSalesChannelsQuotaErrorRequired'),
                      validate: {
                        cannotBeNegative: (value) =>
                          cannotBeNegative({
                            value: value ?? '',
                            message: t('offers:admissionSalesChannelsQuotaErrorNegative'),
                          }),
                        min: (value) =>
                          min({
                            value,
                            minValue: 1,
                            message: t('offers:admissionSalesChannelsQuotaErrorMin', {
                              number: 1,
                            }),
                          }),
                        max: (value) =>
                          max({
                            value,
                            maxValue: quantity,
                            message: t('offers:admissionSalesChannelsQuotaErrorMax', {
                              quantity,
                            }),
                          }),
                        maxDecimals: (value) =>
                          maxDecimals({
                            value: value ?? '',
                            message: t('offers:admissionSalesChannelsQuotaErrorDecimals'),
                            decimals: 0,
                          }),
                      },
                    })}
                  />
                </div>
              </div>
              <ErrorMessage name='limitAllChannels' />
            </>
          )}
        </div>

        <hr className='my-12 border-t border-extra-soft' />

        <div>
          <LabelInfo htmlFor='isNominative' text={t('offers:admissionTicketIsNominativeTooltip')}>
            {t('offers:admissionTicketIsNominativeLabel')}
          </LabelInfo>
          <Controller
            name='isNominative'
            control={control}
            render={({ field }) => {
              return (
                <Toggle
                  isEnabledText={t('offers:admissionTicketIsNominativeTransferable')}
                  isNotEnabledText={t('offers:admissionTicketIsNominativeNominative')}
                  {...field}
                  disabled={hasOfferSoldItems || isPastEvent}
                />
              );
            }}
          />
        </div>

        <div className='mt-8'>
          <InputFormFields
            showModal
            formFieldName={BOOKING_FORM_FIELDS}
            label={t('offers:admissionTicketBookingFormFieldsLabel')}
            fields={bookingFormFields}
            disableConditions={hasOfferSoldItems || isPastEvent}
            appendFields={handleNewTicketBookingFormField}
          />
        </div>

        {isNominative && (
          <div className='mt-8'>
            <InputFormFields
              formFieldName={TICKET_HOLDER_FORM_FIELDS}
              label={t('offers:admissionTicketHolderFormFieldsLabel')}
              fields={ticketHolderFormFields}
              disableConditions={hasOfferSoldItems || isPastEvent}
              appendFields={handleNewTicketHolderFormField}
            />
          </div>
        )}

        {!isClubCountryItaly && <VatRateInput disabled={isPastEvent} readOnly={isPastEvent} />}
      </div>
    </div>
  );
};
